/**
 * Casamento no tempo entre os quadros do loop e os da fumaça, e o frescor do
 * índice — funções puras (testadas em `tempo.test.ts`).
 *
 * O ADPF e as imagens do STAR saem da MESMA varredura do disco completo:
 * ambos começam em :00, :10, :20… e levam o mesmo carimbo. Em regime o
 * casamento é exato. A tolerância de 10 min cobre um quadro da NOAA que
 * falhou (fica o vizinho, a pluma anda pouco em 10 min) e o fim da janela,
 * quando a imagem do satélite chega antes da fumaça; mais longe que isso a
 * fumaça some naquele quadro — nunca um contorno velho com o horário novo.
 */
import { MINUTE_MS } from '../lib/time';
import { varreduraParaQuadro } from '../radar/tempo';
import type { QuadroFumaca } from './api';

export const TOLERANCIA_MS = 10 * MINUTE_MS;

/** O quadro da fumaça a exibir no instante `t` (o mais próximo, até a tolerância), ou null. */
export function quadroParaInstante(quadros: readonly QuadroFumaca[], tempos: readonly number[], t: number | null): QuadroFumaca | null {
  if (t === null || !quadros.length) return null;
  const i = varreduraParaQuadro(tempos, t, TOLERANCIA_MS);
  return i >= 0 ? quadros[i] : null;
}

/**
 * A NOAA publica ~14 min depois da varredura e a máquina olha a cada 2 min:
 * o normal é o mais novo ter 15–25 min. Passando de 45, avisa.
 */
export const ATRASO_AVISO_MIN = 45;
/** Seis horas: o que está no mapa é história. */
export const ATRASO_GRAVE_MIN = 6 * 60;

function quanto(min: number): string {
  return min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
}

/** Aviso sobre o quadro mais novo publicado, ou null quando está em dia. */
export function avisoDeFrescor(ultimo: number | null, agora: number = Date.now()): string | null {
  if (ultimo === null) return null;
  const min = Math.max(0, (agora - ultimo) / MINUTE_MS);
  if (min >= ATRASO_GRAVE_MIN) return `A fumaça está sem quadro novo há ${quanto(min)}. O que está no mapa não é a fumaça de agora.`;
  if (min >= ATRASO_AVISO_MIN) return `Fumaça sem quadro novo há ${quanto(min)} — o normal é 15–25 min depois da imagem.`;
  return null;
}

/** De noite (ou quase) a NOAA não tenta detectar fumaça: cobertura abaixo disto é "sem dado". */
export const COBERTURA_MINIMA = 0.05;

export function semDeteccao(q: QuadroFumaca | null): boolean {
  return q !== null && q.cob !== null && q.cob < COBERTURA_MINIMA;
}
